// @ts-check
import { Plus, Ruler, Save, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../context/ToastContext';
import api from '../utils/apiClient';
import { buildMeasurementPayload } from '../utils/measurementPayload';

const MEASUREMENT_FIELDS = [
  { key: 'bust', label: 'Bust' },
  { key: 'upper_bust', label: 'Upper Bust' },
  { key: 'waist', label: 'Waist' },
  { key: 'hip', label: 'Hip' },
  { key: 'shoulder', label: 'Shoulder' },
  { key: 'length', label: 'Length' },
  { key: 'sleeve', label: 'Sleeve' },
  { key: 'inseam', label: 'Inseam' },
];

const MAIN_PIECE_KEY = 'main';

const MEASUREMENT_FIELD_KEYS = MEASUREMENT_FIELDS.map((f) => f.key);

const emptyGrid = (sizes) =>
  Object.fromEntries(sizes.map((size) => [size, Object.fromEntries(MEASUREMENT_FIELDS.map((f) => [f.key, '']))]));

/**
 * The grid is kept mounted by the parent (hidden, not unmounted) so edits survive tab switches.
 * `onDirty` fires on every edit; `saveHandlerRef.current` is kept pointing at `saveAll`, which
 * the parent's Save calls so unsaved grids are written alongside the rest of the modal.
 * @param {{
 *   productId: string | null,
 *   variants: any[],
 *   additionalPieceTypeIds: string[],
 *   onAdditionalPieceTypeIdsChange?: (ids: string[]) => void,
 *   onDirty?: () => void,
 *   saveHandlerRef?: { current: (() => Promise<void>) | null },
 * }} props
 */
const ProductGarmentMeasurementsManager = ({
  productId,
  variants,
  additionalPieceTypeIds,
  onAdditionalPieceTypeIdsChange,
  onDirty,
  saveHandlerRef,
}) => {
  const toast = useToast();
  const [productTypes, setProductTypes] = useState([]);
  const [measurementsByPiece, setMeasurementsByPiece] = useState({});
  // Always the newest grids, so a save can tell whether a cell was edited while its PUT was in flight.
  const latestGrids = useRef(measurementsByPiece);
  latestGrids.current = measurementsByPiece;
  // Pieces edited since they were loaded or last saved, keyed by piece key.
  const [dirtyPieces, setDirtyPieces] = useState(/** @type {Record<string, true>} */ ({}));
  const [activePiece, setActivePiece] = useState(MAIN_PIECE_KEY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addingPiece, setAddingPiece] = useState(false);
  const [newPieceTypeId, setNewPieceTypeId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [applyingTemplateId, setApplyingTemplateId] = useState('');
  const [savingAsTemplate, setSavingAsTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [managingTemplates, setManagingTemplates] = useState(false);

  const pieceTypeIds = additionalPieceTypeIds || [];

  // Get unique sizes from product variants
  const sizes = [...new Set((variants || []).filter((v) => v.size).map((v) => v.size))];

  const pieces = useMemo(() => {
    const list = [{ key: MAIN_PIECE_KEY, label: 'Main Garment' }];
    for (const id of additionalPieceTypeIds || []) {
      const label = (productTypes || []).find((pt) => pt.id === id)?.name || 'Piece';
      list.push({ key: id, label });
    }
    return list;
  }, [additionalPieceTypeIds, productTypes]);

  const availablePieceTypes = (productTypes || []).filter((pt) => !pieceTypeIds.includes(pt.id));

  // Templates saved for this specific piece, plus generic ones (piece_type_id null,
  // e.g. saved from the Main garment) — a "Bottom" template doesn't clutter "Top".
  const relevantTemplates = templates.filter(
    (t) => !t.piece_type_id || t.piece_type_id === (activePiece === MAIN_PIECE_KEY ? null : activePiece),
  );

  // Reset per-piece-scoped UI state whenever the active piece changes — otherwise
  // the Apply Template <select> keeps its previous piece's selection, which means
  // re-picking the SAME template on a newly added piece never fires onChange.
  useEffect(() => {
    setApplyingTemplateId('');
    setSavingAsTemplate(false);
    setNewTemplateName('');
    setManagingTemplates(false);
  }, [activePiece]);

  const loadMeasurements = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const data = await api.get(`/api/admin/products/${productId}/garment-measurements`);
      const byPiece = {};
      for (const item of data || []) {
        const pieceKey = item.piece_type_id || MAIN_PIECE_KEY;
        if (!byPiece[pieceKey]) byPiece[pieceKey] = {};
        byPiece[pieceKey][item.size_name] = {};
        for (const field of MEASUREMENT_FIELDS) {
          byPiece[pieceKey][item.size_name][field.key] = item[field.key] ?? '';
        }
      }
      setMeasurementsByPiece(byPiece);
    } catch {
      // No measurements yet — that's fine
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await api.get('/api/admin/garment-measurement-templates');
      setTemplates(data || []);
    } catch {
      // Non-critical
    }
  }, []);

  const loadProductTypes = useCallback(async () => {
    try {
      const data = await api.get('/api/admin/taxonomy/product-types');
      setProductTypes((data || []).filter((t) => t.is_active));
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    loadMeasurements();
    loadTemplates();
    loadProductTypes();
  }, [loadMeasurements, loadTemplates, loadProductTypes]);

  const activeGrid = measurementsByPiece[activePiece] || {};

  const markDirty = (pieceKey) => {
    setDirtyPieces((prev) => (prev[pieceKey] ? prev : { ...prev, [pieceKey]: true }));
    onDirty?.();
  };

  const handleChange = (sizeName, fieldKey, value) => {
    markDirty(activePiece);
    setMeasurementsByPiece((prev) => ({
      ...prev,
      [activePiece]: {
        ...(prev[activePiece] || {}),
        [sizeName]: {
          ...((prev[activePiece] || {})[sizeName] || {}),
          [fieldKey]: value,
        },
      },
    }));
  };

  // Writes every edited piece (Main + extras), not just the one on screen. Throws on failure
  // so callers decide how to surface it; a piece stays dirty until its own PUT succeeds.
  const saveAll = async () => {
    // No sizes means no variants left, and an empty payload would delete the piece's stored rows.
    if (sizes.length === 0) return;
    for (const pieceKey of Object.keys(dirtyPieces)) {
      // A piece removed elsewhere (Composition tab) must not be written back as orphan rows.
      if (!pieces.some((p) => p.key === pieceKey)) continue;
      const grid = measurementsByPiece[pieceKey];
      const payload = buildMeasurementPayload(sizes, grid, MEASUREMENT_FIELD_KEYS);
      const query = pieceKey !== MAIN_PIECE_KEY ? `?piece_type_id=${pieceKey}` : '';
      await api.put(`/api/admin/products/${productId}/garment-measurements${query}`, payload);
      // Only clear the flag if the grid is unchanged since the payload was built; a cell edited
      // during the PUT stays dirty so the next Save writes it.
      if (latestGrids.current[pieceKey] !== grid) continue;
      setDirtyPieces((prev) => {
        const next = { ...prev };
        delete next[pieceKey];
        return next;
      });
    }
  };

  // The active piece can disappear underneath us (removed on the Composition tab).
  useEffect(() => {
    if (!pieces.some((p) => p.key === activePiece)) setActivePiece(MAIN_PIECE_KEY);
  }, [pieces, activePiece]);

  // Refreshed every render so the parent always calls the closure with the latest grid state.
  useEffect(() => {
    if (!saveHandlerRef) return undefined;
    saveHandlerRef.current = saveAll;
    return () => {
      saveHandlerRef.current = null;
    };
  });

  const handleSave = async () => {
    if (Object.keys(dirtyPieces).length === 0) {
      toast.success('No measurement changes to save');
      return;
    }
    setSaving(true);
    try {
      await saveAll();
      toast.success('Garment measurements saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save measurements');
    } finally {
      setSaving(false);
    }
  };

  const persistPieceTypeIds = async (ids) => {
    await api.put(`/api/admin/products/${productId}`, { additional_piece_type_ids: ids });
    onAdditionalPieceTypeIdsChange?.(ids);
  };

  const handleAddPiece = async () => {
    if (!newPieceTypeId) return;
    const nextIds = [...pieceTypeIds, newPieceTypeId];
    try {
      await persistPieceTypeIds(nextIds);
      setActivePiece(newPieceTypeId);
      setNewPieceTypeId('');
      setAddingPiece(false);
    } catch (err) {
      toast.error(err.message || 'Failed to add piece');
    }
  };

  const handleRemovePiece = async (pieceKey) => {
    if (!window.confirm('Remove this piece and its measurements?')) return;
    try {
      await api.put(`/api/admin/products/${productId}/garment-measurements?piece_type_id=${pieceKey}`, []);
      const nextIds = pieceTypeIds.filter((id) => id !== pieceKey);
      await persistPieceTypeIds(nextIds);
      setMeasurementsByPiece((prev) => {
        const next = { ...prev };
        delete next[pieceKey];
        return next;
      });
      setDirtyPieces((prev) => {
        const next = { ...prev };
        delete next[pieceKey];
        return next;
      });
      if (activePiece === pieceKey) setActivePiece(MAIN_PIECE_KEY);
    } catch (err) {
      toast.error(err.message || 'Failed to remove piece');
    }
  };

  const handleApplyTemplate = async (templateId) => {
    if (!templateId) return;
    setApplyingTemplateId(templateId);
    try {
      const template = await api.get(`/api/admin/garment-measurement-templates/${templateId}`);
      const valuesBySize = Object.fromEntries((template.values || []).map((v) => [v.size_name, v]));
      markDirty(activePiece);
      setMeasurementsByPiece((prev) => {
        const nextGrid = { ...(prev[activePiece] || emptyGrid(sizes)) };
        for (const size of sizes) {
          const tv = valuesBySize[size];
          if (!tv) continue;
          nextGrid[size] = {
            ...(nextGrid[size] || {}),
            ...Object.fromEntries(MEASUREMENT_FIELDS.map((f) => [f.key, tv[f.key] ?? ''])),
          };
        }
        return { ...prev, [activePiece]: nextGrid };
      });
      toast.success(`Applied "${template.name}" — review and save`);
    } catch (err) {
      toast.error(err.message || 'Failed to apply template');
    } finally {
      // Reset immediately — the <select> should always be ready for the next pick,
      // including re-applying the same template to a different piece.
      setApplyingTemplateId('');
    }
  };

  const handleSaveAsTemplate = async () => {
    const name = newTemplateName.trim();
    if (!name) return;
    setSavingTemplate(true);
    try {
      const values = sizes.map((size) => ({
        size_name: size,
        ...Object.fromEntries(
          MEASUREMENT_FIELDS.map((f) => [f.key, activeGrid[size]?.[f.key] === '' ? null : activeGrid[size]?.[f.key]]),
        ),
      }));
      const pieceTypeId = activePiece === MAIN_PIECE_KEY ? null : activePiece;
      await api.post('/api/admin/garment-measurement-templates', { name, piece_type_id: pieceTypeId, values });
      toast.success(`Template "${name}" saved`);
      setNewTemplateName('');
      setSavingAsTemplate(false);
      loadTemplates();
    } catch (err) {
      toast.error(err.message || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await api.del(`/api/admin/garment-measurement-templates/${templateId}`);
      if (applyingTemplateId === templateId) setApplyingTemplateId('');
      loadTemplates();
    } catch (err) {
      toast.error(err.message || 'Failed to delete template');
    }
  };

  if (!productId) {
    return (
      <div className="text-center py-12 text-muted-foreground/70 text-sm">
        Save the product first to add garment measurements.
      </div>
    );
  }

  if (sizes.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground/70 text-sm">
        Add product variants first — garment measurements are entered per size.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Ruler size={20} />
        <h3 className="text-lg font-medium">Garment Measurements</h3>
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        Enter actual garment dimensions per size. All values in inches. Leave empty for fields that don't apply. For
        co-ords, dresses with a slip, or anything with more than one part, add each part as its own piece below.
      </p>

      {/* Piece tabs */}
      <div className="flex items-center flex-wrap gap-2 mb-4">
        {pieces.map((piece) => (
          <div
            key={piece.key}
            className={`flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full text-sm border ${
              activePiece === piece.key
                ? 'bg-primary text-primary-foreground border-foreground'
                : 'bg-card text-foreground/80 border-input hover:border-muted-foreground'
            }`}
          >
            <button type="button" onClick={() => setActivePiece(piece.key)}>
              {piece.label}
            </button>
            {piece.key !== MAIN_PIECE_KEY && (
              <button
                type="button"
                onClick={() => handleRemovePiece(piece.key)}
                className={
                  activePiece === piece.key
                    ? 'text-white/70 hover:text-white'
                    : 'text-muted-foreground/70 hover:text-foreground'
                }
                title="Remove piece"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}

        {addingPiece ? (
          <div className="flex items-center gap-1.5">
            <select
              value={newPieceTypeId}
              onChange={(e) => setNewPieceTypeId(e.target.value)}
              className="px-2 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
            >
              <option value="">Select piece type...</option>
              {availablePieceTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAddPiece}
              disabled={!newPieceTypeId}
              className="px-2 py-1.5 bg-primary text-primary-foreground text-xs rounded-md disabled:opacity-50"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setAddingPiece(false);
                setNewPieceTypeId('');
              }}
              className="text-muted-foreground/70 hover:text-foreground"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddingPiece(true)}
            disabled={availablePieceTypes.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border border-dashed border-input text-muted-foreground hover:border-muted-foreground hover:text-foreground disabled:opacity-40"
            title={
              availablePieceTypes.length === 0
                ? 'All product types already added'
                : 'Add another piece (e.g. Top, Bottom, Slip)'
            }
          >
            <Plus size={14} /> Add piece
          </button>
        )}
      </div>

      {/* Templates + save */}
      <div className="flex items-center flex-wrap gap-2 mb-2">
        <select
          value={applyingTemplateId}
          onChange={(e) => handleApplyTemplate(e.target.value)}
          className="px-2 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
        >
          <option value="">Apply template...</option>
          {relevantTemplates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>

        {savingAsTemplate ? (
          <>
            <input
              type="text"
              autoFocus
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="New template name"
              className="px-2 py-1.5 border border-input rounded-lg text-sm w-40 focus:outline-none focus:border-foreground"
            />
            <button
              type="button"
              onClick={handleSaveAsTemplate}
              disabled={savingTemplate || !newTemplateName.trim()}
              className="px-3 py-1.5 bg-primary text-primary-foreground text-sm rounded-lg disabled:opacity-50"
            >
              {savingTemplate ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setSavingAsTemplate(false);
                setNewTemplateName('');
              }}
              className="text-muted-foreground/70 hover:text-foreground"
            >
              <X size={16} />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setSavingAsTemplate(true)}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            + Save as new template
          </button>
        )}

        <button
          type="button"
          onClick={() => setManagingTemplates((v) => !v)}
          className="text-xs text-muted-foreground/70 hover:text-foreground"
        >
          Manage templates
        </button>

        <div className="flex-1" />

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50"
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Measurements'}
        </button>
      </div>

      {managingTemplates && (
        <div className="mb-4 border border-border rounded-lg divide-y divide-border/60">
          {templates.length === 0 ? (
            <div className="text-xs text-muted-foreground/70 px-3 py-2">No templates yet.</div>
          ) : (
            templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span>
                  {t.name}
                  <span className="text-muted-foreground/70 ml-1.5">({t.piece_type_name || 'any piece'})</span>
                </span>
                <button
                  type="button"
                  onClick={() => handleDeleteTemplate(t.id)}
                  className="text-xs text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400"
                >
                  Delete
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground/70 text-sm">Loading...</div>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase w-32">
                  Measurement
                </th>
                {sizes.map((size) => (
                  <th key={size} className="text-center py-3 px-3 font-medium text-foreground text-sm min-w-[80px]">
                    {size}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {MEASUREMENT_FIELDS.map((field) => (
                <tr key={field.key} className="hover:bg-muted/50">
                  <td className="py-2 px-4 font-medium text-foreground/80 text-sm">{field.label}</td>
                  {sizes.map((size) => (
                    <td key={size} className="py-2 px-2">
                      <input
                        type="number"
                        step="any"
                        value={activeGrid[size]?.[field.key] ?? ''}
                        onChange={(e) => handleChange(size, field.key, e.target.value)}
                        className="w-full px-2 py-1.5 border border-border rounded-lg text-center text-sm focus:outline-none focus:border-foreground"
                        placeholder="—"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductGarmentMeasurementsManager;
